import { IDocument } from '../../Interfaces/IDocument';
import { IMIMEType } from '../../Interfaces/IMIMEType';
import { getFetch } from '../../hooks/useFetch';

/**
 * Base64 aus `/document/{id}` in einen Blob.
 *
 * Stueckweise statt ueber ein `Array` mit einem Eintrag je Byte: die
 * Ausweisfotos aus dem Ident-Service sind 4-5 MB gross, dafuer entstuende ein
 * Array mit mehreren Millionen Zahlen. Das Ergebnis ist identisch, nur ohne
 * den Speicherberg.
 */
export const base64ToBlob = (data: string, mimeType: string): Blob => {
  const byteCharacters = atob(data);
  const chunkSize = 8192;
  const chunkArray: Uint8Array[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += chunkSize) {
    const slice = byteCharacters.slice(offset, offset + chunkSize);
    const bytes = new Uint8Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      bytes[i] = slice.charCodeAt(i);
    }

    chunkArray.push(bytes);
  }

  return new Blob(chunkArray, { type: mimeType });
};

export const getMimeTypeString = (
  idMIMEType: number,
  mimeTypeArray: IMIMEType[]
): string =>
  mimeTypeArray.find(mimeType => mimeType.idMIMEType === idMIMEType)
    ?.MIMEType ?? 'application/octet-stream';

export const getFileExtension = (
  fileName: string,
  idMIMEType: number,
  mimeTypeArray: IMIMEType[]
): string => {
  const dotIndex = fileName.lastIndexOf('.');

  if (dotIndex > 0 && dotIndex < fileName.length - 1) {
    return fileName.slice(dotIndex + 1).toLowerCase();
  }

  return (
    mimeTypeArray
      .find(mimeType => mimeType.idMIMEType === idMIMEType)
      ?.FileExtension?.toLowerCase() ?? ''
  );
};

export const triggerBrowserDownload = (blob: Blob, fileName: string) => {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(blobUrl);
};

/**
 * Laedt ein Dokument herunter. Ist der Inhalt (`Data`) noch nicht geladen, wird
 * er zuvor ueber `/document/{id}` nachgeladen.
 */
export const downloadDocument = (
  documentObject: Pick<IDocument, 'idDocument' | 'FileName' | 'idMIMEType'> & {
    Data?: string;
  },
  mimeTypeArray: IMIMEType[],
  setIsLoading?: (isLoading: boolean) => void
) => {
  const mimeTypeString = getMimeTypeString(
    documentObject.idMIMEType,
    mimeTypeArray
  );

  if (documentObject.Data !== undefined && documentObject.Data !== null) {
    triggerBrowserDownload(
      base64ToBlob(documentObject.Data, mimeTypeString),
      documentObject.FileName
    );

    return;
  }

  getFetch(
    '/document/',
    documentObject.idDocument,
    (loaded: IDocument) => {
      if (
        loaded !== null &&
        loaded !== undefined &&
        loaded.Data !== undefined
      ) {
        triggerBrowserDownload(
          base64ToBlob(
            loaded.Data,
            getMimeTypeString(loaded.idMIMEType, mimeTypeArray)
          ),
          loaded.FileName
        );
      }
    },
    undefined,
    undefined,
    setIsLoading
  );
};
